import { Box, Chip, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import { getObjectSettingsByType } from '@/endpoints'
import { useApi } from '@/endpoints/hooks'
import { ObjectSettingsUpdateDto } from '@/endpoints/models'
import { IdCodeName } from '@/endpoints/schemas'
import { useAppContext } from '@/hooks'

const inputFontSizeSx = { '& .MuiSelect-select': { fontSize: '1rem' } }

/** Match Stereotype select height (40px). Override any default MuiSelect-select padding (e.g. 64px) with 0. */
const HEIGHT_TARGET = 40
const matchSelectHeightSx = {
	'& .MuiOutlinedInput-root': {
		height: HEIGHT_TARGET,
		minHeight: HEIGHT_TARGET,
		'& .MuiSelect-select': {
			paddingTop: '0 !important',
			paddingBottom: '0 !important',
			boxSizing: 'border-box',
			height: HEIGHT_TARGET,
			display: 'flex',
			alignItems: 'center',
		},
	},
}

export interface ObjectSettingsMultiselectV2Props {
	data?: IdCodeName[]
	disabled?: boolean
	highlightSx?: SxProps<Theme>
	onChange?: (newValue: IdCodeName[]) => void
	type: ObjectSettingsUpdateDto.StructureTypeEnum
}

/**
 * Multiselect dropdown for object settings using MUI Select (matches Stereotype and other fields).
 * Alternative to ObjectSettingsControlV2 (chips + add-one-by-one); kept for backup.
 */
export const ObjectSettingsMultiselectV2 = ({
	data = [],
	disabled,
	onChange,
	type,
	highlightSx,
}: ObjectSettingsMultiselectV2Props) => {
	const { t } = useAppContext()
	const objectSettings = useApi(getObjectSettingsByType(type))
	const options = objectSettings.data ?? []

	const selectedIds = data.map((o) => o.id).filter((id): id is number => id != null)

	const handleChange = (e: { target: { value: unknown } }) => {
		const value = e.target.value
		const ids = typeof value === 'string' ? [] : (value as number[])
		const newValue = ids
			.map((id) => options.find((o) => o.id === id))
			.filter((o): o is IdCodeName => o != null)
			.map((o) => ({ id: o.id, code: o.code, name: o.name }))
		onChange?.(newValue)
	}

	const getOptionLabel = (o: IdCodeName) => o.name ?? o.code ?? ''

	return (
		<FormControl
			size="small"
			disabled={disabled}
			fullWidth
			sx={[inputFontSizeSx, matchSelectHeightSx, ...(highlightSx ? [highlightSx] : [])]}
		>
			<InputLabel>{t('OBJECTS')}</InputLabel>
			<Select
				multiple
				value={selectedIds}
				onChange={handleChange}
				label={t('OBJECTS')}
				renderValue={(ids) => (
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 0.5,
							margin: 0,
							lineHeight: 1,
							alignItems: 'center',
							'& .MuiChip-root': { margin: 0 },
						}}
					>
						{(ids as number[]).map((id) => {
							const option = options.find((o) => o.id === id)
							if (!option) return null
							return (
								<Chip
									key={id}
									label={getOptionLabel(option)}
									size="small"
									variant="outlined"
									sx={{
										borderColor: 'divider',
										'& .MuiChip-label': { fontSize: '0.875rem' },
										margin: 0,
									}}
								/>
							)
						})}
					</Box>
				)}
			>
				{options.map((option) => (
					<MenuItem key={option.id ?? option.code} value={option.id}>
						{getOptionLabel(option)}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	)
}
